/**
 * Antrean Mutasi Bank Belum Teralokasi (spec 2026-10-22 "TUNTASKAN SINKRONISASI" §2).
 *
 * Memakai dialog klasifikasi TERPADU `ReceiptClassifyDialog` (mode bank_row) —
 * kontrak & tampilan sama dengan Antrean Penerimaan. Alokasi penuh/parsial/
 * split ke invoice resmi langsung dari dialog via POST
 * /api/ar/unallocated-receipts/{row_id}/resolve (atomik + idempotent).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Info, RefreshCw, ChevronRight } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import ReceiptClassifyDialog from "./ReceiptClassifyDialog";

const STATUS_META = {
  unallocated: { label: "Belum Dialokasikan", cls: "bg-amber-50 border-amber-300 text-amber-900" },
  partially_allocated: { label: "Sebagian Dialokasikan", cls: "bg-sky-50 border-sky-300 text-sky-800" },
  fully_allocated: { label: "Teralokasi Penuh", cls: "bg-emerald-50 border-emerald-300 text-emerald-800" },
  needs_review: { label: "Perlu Peninjauan", cls: "bg-rose-50 border-rose-300 text-rose-800" },
  non_ar_classified: { label: "Non-A.R.", cls: "bg-slate-100 border-slate-300 text-slate-700" },
};

export default function BankUnallocatedQueue() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setErr(null);
    const q = new URLSearchParams();
    if (status) q.set("status", status);
    api.get(`/ar/unallocated-receipts/queue${q.toString() ? "?" + q.toString() : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => { setErr(formatDetail(e.response?.data?.detail) || "Gagal memuat."); setData(null); })
      .finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const stats = data?.stats || {};
  const items = data?.items || [];

  /** Map baris mutasi → shape item dialog terpadu (source_kind="bank_row"). */
  const toDialogItem = (it) => ({
    source_kind: "bank_row",
    row_id: it.row_id,
    receipt_key: `BANK::${it.row_id}`,
    amount: it.remaining_unallocated_amount,
    full_amount: it.transaction_amount,
    allocated_amount: it.allocated_amount,
    date: it.date,
    bank_name: it.bank_account,
    account_no: null,
    transfer_ref: it.bank_memo,
    sender_name: it.sender_name,
    project_name: null,
    client: it.sender_name,
    invoice_number: null,
    classification: it.canonical_classification
      || (it.allocation_status === "non_ar_classified" ? "non_ar_receipt" : "unclassified"),
    reason: it.canonical_reason || "",
    non_ar_kind: it.non_ar_kind,
    split_allocations: it.canonical_splits || [],
    overridden_by_fat: !!it.canonical_classification || !!it.non_ar_kind,
    is_duplicate_hint: it.duplicate_hint,
  });

  return (
    <div className="space-y-3" data-testid="bank-unallocated-queue">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display text-sm uppercase tracking-wider text-[#0B1D3A]">Mutasi Bank Belum Teralokasi</h2>
        <button onClick={load} disabled={loading} data-testid="bank-queue-reload"
          className="h-7 px-2.5 border border-slate-300 text-[10px] uppercase tracking-wider font-semibold hover:bg-slate-50 flex items-center gap-1">
          <RefreshCw size={10} className={loading ? "animate-spin" : ""} /> Muat Ulang
        </button>
      </div>

      {err && <div className="border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700">{err}</div>}

      {/* Stats cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-1.5" data-testid="bank-queue-stats">
          <div className="border border-slate-200 bg-white p-2" data-testid="bank-stat-total-remaining">
            <div className="text-[9px] uppercase tracking-wider text-slate-500">Total Belum Teralokasi</div>
            <div className="font-display text-sm tabular-nums text-amber-700 mt-0.5">{formatIDR(stats.total_remaining || 0)}</div>
            <div className="text-[9px] text-slate-400">{stats.active_count || 0} mutasi aktif</div>
          </div>
          <div className="border border-slate-200 bg-white p-2"><div className="text-[9px] uppercase tracking-wider text-slate-500">Belum Dialokasikan</div><div className="font-display text-sm tabular-nums text-[#0B1D3A] mt-0.5">{stats.unallocated_count || 0}</div></div>
          <div className="border border-slate-200 bg-white p-2"><div className="text-[9px] uppercase tracking-wider text-slate-500">Sebagian</div><div className="font-display text-sm tabular-nums text-sky-700 mt-0.5">{stats.partially_allocated_count || 0}</div></div>
          <div className="border border-slate-200 bg-white p-2"><div className="text-[9px] uppercase tracking-wider text-slate-500">Perlu Peninjauan</div><div className="font-display text-sm tabular-nums text-rose-700 mt-0.5">{stats.needs_review_count || 0}</div></div>
          <div className="border border-slate-200 bg-white p-2"><div className="text-[9px] uppercase tracking-wider text-slate-500">Non-A.R.</div><div className="font-display text-sm tabular-nums text-slate-600 mt-0.5">{stats.non_ar_classified_count || 0}</div></div>
          <div className="border border-slate-200 bg-white p-2"><div className="text-[9px] uppercase tracking-wider text-slate-500">Mutasi Tertua</div><div className="font-display text-sm tabular-nums text-[#0B1D3A] mt-0.5">{stats.oldest_mutation_date || "—"}</div></div>
        </div>
      )}

      {/* Status filter */}
      <div className="flex items-center gap-1 border-b border-slate-200 flex-wrap">
        {[["", "Semua Aktif"], ["unallocated", "Belum Dialokasikan"], ["partially_allocated", "Sebagian"], ["needs_review", "Perlu Peninjauan"], ["non_ar_classified", "Non-A.R."], ["fully_allocated", "Teralokasi Penuh"]].map(([k, l]) => (
          <button key={k || "all"} type="button" onClick={() => setStatus(k)} data-testid={`bank-status-${k || "all"}`}
            className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold border-b-2 ${status === k ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}>{l}</button>
        ))}
      </div>

      {/* Items table */}
      <div className="border border-slate-200 bg-white overflow-x-auto max-h-[55vh] overflow-y-auto" data-testid="bank-queue-table">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Tanggal</th>
              <th className="text-left px-3 py-2">Bank · Pengirim</th>
              <th className="text-left px-3 py-2">Berita</th>
              <th className="text-right px-3 py-2">Nominal</th>
              <th className="text-right px-3 py-2">Teralokasi</th>
              <th className="text-right px-3 py-2">Sisa</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Umur</th>
              <th className="text-right px-3 py-2 pr-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400 italic" data-testid="bank-queue-empty">Tidak ada mutasi dalam filter ini.</td></tr>
            )}
            {items.map((it) => {
              const meta = STATUS_META[it.allocation_status] || { label: it.allocation_status, cls: "" };
              return (
                <tr key={it.row_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`bank-row-${it.row_id}`}>
                  <td className="px-3 py-1.5 tabular-nums text-slate-600">{it.date ? fmtDate(it.date) : "Belum diketahui"}</td>
                  <td className="px-3 py-1.5 text-[11px]"><div>{it.bank_account || "Belum diketahui"}</div><div className="text-[10px] text-slate-500">{it.sender_name || "Belum diketahui"}</div></td>
                  <td className="px-3 py-1.5 text-[10px] max-w-[220px] truncate" title={it.bank_memo}>{it.bank_memo || "—"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{formatIDR(it.transaction_amount)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatIDR(it.allocated_amount)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-amber-700">{formatIDR(it.remaining_unallocated_amount)}</td>
                  <td className="px-3 py-1.5">
                    <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${meta.cls}`}>{meta.label}</span>
                    {it.canonical_classification_label && (
                      <div className="text-[9px] text-indigo-700 font-semibold mt-0.5" data-testid={`bank-canon-${it.row_id}`}>
                        {it.canonical_classification_label}{it.canonical_by ? ` · ${it.canonical_by}` : ""}
                      </div>
                    )}
                    {it.non_ar_kind_label && !it.canonical_classification_label && (
                      <div className="text-[9px] text-slate-500 mt-0.5">{it.non_ar_kind_label}</div>
                    )}
                    {it.duplicate_hint && <div className="text-[9px] text-rose-700 inline-flex items-center gap-0.5 mt-0.5"><AlertTriangle size={9} /> Duplikat</div>}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{it.age_days ?? "—"} hari</td>
                  <td className="px-3 py-1.5 text-right pr-3">
                    {data?.can_classify_non_ar && (
                      <button type="button" data-testid={`bank-classify-btn-${it.row_id}`}
                        onClick={() => setSel(toDialogItem(it))}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white">
                        {it.canonical_classification || it.non_ar_kind ? "Ubah" : "Klasifikasi"} <ChevronRight size={10} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-slate-500 italic flex items-start gap-1" data-testid="bank-queue-footer">
        <Info size={10} className="mt-0.5 shrink-0" />
        Total aktif: <b className="tabular-nums">{formatIDR(stats.total_remaining || 0)}</b> ·
        Dialog klasifikasi terpadu: alokasi penuh/parsial/split ke invoice resmi dapat dilakukan langsung dari sini (atomik, idempotent, audit trail).
      </div>

      {sel && (
        <ReceiptClassifyDialog
          item={sel}
          onClose={() => setSel(null)}
          onSaved={() => { setSel(null); load(); toast.success("Klasifikasi tersimpan."); }}
        />
      )}
    </div>
  );
}
